import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle, AlertTriangle, XCircle, Database, FileText, HelpCircle } from 'lucide-react';

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical';
  total_modules: number;
  enabled_modules: number;
  orphaned_modules: number;
  orphaned_content: number;
  orphaned_quizzes: number;
  modules_with_duration: number;
  modules_missing_duration: number;
  duration_coverage_pct: number;
  modules_with_content: number;
  modules_missing_content: number;
  content_coverage_pct: number;
  modules_with_quiz: number;
  modules_missing_quiz: number;
  quiz_coverage_pct: number;
  module_count_mismatches: number;
  archived_content_count: number;
  last_check: string;
}

export default function SystemHealth() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_system_health');
      
      if (error) throw error;
      
      if (data && typeof data === 'object') {
        setHealth(data as unknown as SystemHealth);
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
      toast.error('Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
      case 'critical': return <XCircle className="h-6 w-6 text-red-600" />;
      default: return <AlertTriangle className="h-6 w-6 text-gray-600" />;
    }
  };

  if (loading && !health) {
    return (
      <div className="container mx-auto py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-32 bg-muted rounded" />
            <div className="h-32 bg-muted rounded" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">System Health Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Real-time monitoring of database integrity and content coverage
          </p>
        </div>
        <Button onClick={fetchHealth} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {health && (
        <>
          {/* Overall Status */}
          <Card className={`p-6 ${getStatusColor(health.status)}`}>
            <div className="flex items-center gap-4">
              {getStatusIcon(health.status)}
              <div className="flex-1">
                <h2 className="text-2xl font-bold capitalize">{health.status}</h2>
                <p className="text-sm mt-1">
                  Last checked: {new Date(health.last_check).toLocaleString()}
                </p>
              </div>
              <Badge variant={health.status === 'healthy' ? 'default' : 'destructive'} className="text-lg px-4 py-2">
                {health.enabled_modules} Active Modules
              </Badge>
            </div>
          </Card>

          {/* Critical Issues */}
          {(health.orphaned_modules > 0 || health.orphaned_content > 0 || health.orphaned_quizzes > 0 || health.module_count_mismatches > 0) && (
            <Card className="p-6 border-red-500 bg-red-50">
              <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Critical Issues Detected
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {health.orphaned_modules > 0 && (
                  <div className="text-center p-3 bg-white rounded">
                    <div className="text-2xl font-bold text-red-600">{health.orphaned_modules}</div>
                    <div className="text-sm text-red-900">Orphaned Modules</div>
                  </div>
                )}
                {health.orphaned_content > 0 && (
                  <div className="text-center p-3 bg-white rounded">
                    <div className="text-2xl font-bold text-red-600">{health.orphaned_content}</div>
                    <div className="text-sm text-red-900">Orphaned Content</div>
                  </div>
                )}
                {health.orphaned_quizzes > 0 && (
                  <div className="text-center p-3 bg-white rounded">
                    <div className="text-2xl font-bold text-red-600">{health.orphaned_quizzes}</div>
                    <div className="text-sm text-red-900">Orphaned Quizzes</div>
                  </div>
                )}
                {health.module_count_mismatches > 0 && (
                  <div className="text-center p-3 bg-white rounded">
                    <div className="text-2xl font-bold text-red-600">{health.module_count_mismatches}</div>
                    <div className="text-sm text-red-900">Count Mismatches</div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Coverage Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Content Coverage */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-8 w-8 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">Content Coverage</h3>
                  <p className="text-2xl font-bold">{health.content_coverage_pct}%</p>
                </div>
              </div>
              <Progress value={health.content_coverage_pct} className="mb-2" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{health.modules_with_content} with content</span>
                <span>{health.modules_missing_content} missing</span>
              </div>
            </Card>

            {/* Quiz Coverage */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <HelpCircle className="h-8 w-8 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">Quiz Coverage</h3>
                  <p className="text-2xl font-bold">{health.quiz_coverage_pct}%</p>
                </div>
              </div>
              <Progress value={health.quiz_coverage_pct} className="mb-2" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{health.modules_with_quiz} with quizzes</span>
                <span>{health.modules_missing_quiz} missing</span>
              </div>
            </Card>

            {/* Duration Coverage */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Database className="h-8 w-8 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">Duration Coverage</h3>
                  <p className="text-2xl font-bold">{health.duration_coverage_pct}%</p>
                </div>
              </div>
              <Progress value={health.duration_coverage_pct} className="mb-2" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{health.modules_with_duration} with duration</span>
                <span>{health.modules_missing_duration} missing</span>
              </div>
            </Card>
          </div>

          {/* Archive Info */}
          <Card className="p-6 bg-blue-50 border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Archived Content</h3>
            <p className="text-blue-700">
              {health.archived_content_count} items archived and available for AI Tutor and Interview Prep
            </p>
          </Card>

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/admin/quiz-generation'}
              >
                Generate Missing Quizzes
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/admin/module-duration-fix'}
              >
                Fix Module Durations
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/admin/content-quality'}
              >
                Content Quality Report
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
